// =============================================================================
// LIGA AJUPAM — A dónde lleva una notificación (v6.11.66)
// =============================================================================
// Una notificación llega por tres caminos y los tres tienen que aterrizar en la
// MISMA pantalla:
//
//   push      El Service Worker abre `https://liga.ajupam.ar<action_url>` →
//             App.tsx lee el pathname al arrancar.
//   WhatsApp  El botón de la plantilla usa el mismo `action_url` como sufijo
//             (ver supabase/functions/send-whatsapp-notification).
//   campanita El drawer no navega por URL sino por `view` (estado en App.tsx).
//
// Antes cada camino resolvía por su cuenta y ninguno resolvía bien: la campanita
// hacía `onNavigate(action_url.replace('/',''))` — o sea `onNavigate('match-center')`,
// que no es ninguna vista y caía al default (Inicio) —, sólo atendía el
// `metadata.target === 'profile'` de las fotos rechazadas, e ignoraba
// `related_match_id`. El Service Worker, por su lado, mandaba a `/dashboard`
// todo lo que no tuviera `action_url`, que era el 96% de las notificaciones.
//
// Acá vive el único mapa: path ↔ vista, y notificación → destino. La contraparte
// en SQL es `public.notification_action_url(...)` (migración v6.11.66), que
// completa `notifications.action_url` cuando el emisor no lo puso. Si se agrega
// una ruta o un tipo de notificación, tocar los dos lados.
//
// -----------------------------------------------------------------------------
// v6.11.91 — Abrir un partido NO es lo mismo que señalarlo
// -----------------------------------------------------------------------------
// v6.11.66 resolvió el destino mirando el ESTADO del partido: propuesta sin
// confirmar → agenda, todo lo demás → `/partido/<id>`, o sea MatchCenter. Con eso,
// "Partido Confirmado · el equipo rival confirmó el partido" aterrizaba en la
// pantalla de CARGAR EL RESULTADO de un partido que todavía no se jugó — que es
// exactamente lo que el reglamento no permite hacer todavía (hay ventana horaria
// y foto de los 4 jugadores).
//
// El estado del partido no alcanza: lo que decide es de qué habla la notificación.
// Las de programación (`AGENDA_ONLY_MATCH_TYPES`) llevan a "Mi Agenda" con el
// partido RESALTADO (`highlightMatchId`) y nada más; las de resultado siguen
// abriendo MatchCenter (`matchId`).

/** Destino resuelto dentro del shell del participante. */
export interface NotificationTarget {
  /** Vista del switch de App.tsx (`renderView`). */
  view: string;
  /** Presente sólo cuando la notificación apunta a UN partido → abre MatchCenter. */
  matchId?: string;
  /**
   * v6.11.91 — El partido al que se refiere la notificación, para RESALTARLO en
   * la agenda sin abrir nada. Excluyente con `matchId`: uno abre la pantalla de
   * resultado, el otro sólo señala la tarjeta.
   */
  highlightMatchId?: string;
  /**
   * v6.11.225 — Evento intermedio de la liga (`/evento/<uuid>`): abre su
   * pantalla (vista `league-event`).
   */
  eventId?: string;
}

/** La forma mínima de una notificación para resolver su destino. */
export interface NotificationTargetInput {
  type?: string | null;
  action_url?: string | null;
  metadata?: Record<string, unknown> | null;
  related_match_id?: string | null;
}

// Primer segmento del path → vista. Todos estos segmentos tienen que estar en
// RESERVED_SLUGS (constants/reservedSlugs.ts) y en `public._is_slug_reserved`:
// si no, una liga podría llamarse igual y el catch-all de landings se los come.
const PATH_TO_VIEW: Record<string, string> = {
  agenda: 'schedule-agenda',
  fixture: 'schedule',
  partidos: 'schedule',
  'nuevo-partido': 'schedule',
  perfil: 'profile',
  profile: 'profile',
  'mi-equipo': 'team',
  tabla: 'standings',
  standings: 'standings',
  playoffs: 'playoffs-bracket',
  convenios: 'convenios',
  dashboard: 'dashboard',
  inicio: 'dashboard',
  // Legacy: 18.000 notificaciones viven con `/match-center`, que nunca fue una
  // ruta. Son todas de resultado (pre/post partido, recordatorios), así que van
  // a la agenda —y a MatchCenter si traen `related_match_id`, ver más abajo.
  'match-center': 'schedule-agenda',
};

// Tipo de notificación → vista, cuando no hay nada más específico.
const TYPE_TO_VIEW: Record<string, string> = {
  MATCH: 'schedule-agenda',
  MATCH_PROPOSED: 'schedule-agenda',
  MATCH_CONFIRMED: 'schedule-agenda',
  MATCH_MODIFIED: 'schedule-agenda',
  MATCH_CANCELLED: 'schedule-agenda',
  PARTIDO_PROXIMO: 'schedule-agenda',
  RESULTADO_PENDIENTE: 'schedule-agenda',
  // v6.11.126 — El resultado ya está cerrado: se va a la ficha del partido,
  // que es donde está la placa para compartir. Por eso NO entran en
  // AGENDA_ONLY_MATCH_TYPES (ésos sólo resaltan la tarjeta en la agenda).
  MATCH_RESULT_WIN: 'schedule-agenda',
  MATCH_RESULT_LOSS: 'schedule-agenda',
  SCHEDULE_PUBLISHED: 'playoffs-bracket',
  PAYMENT: 'profile',
  PAGO_VENCE: 'profile',
  EQUIPO_COMPLETO: 'team',
  WITHDRAWAL: 'team',
  // v6.11.156 — El desafío recibido y su recordatorio aterrizan en el INICIO,
  // que es donde vive la tarjeta con el countdown y el botón para fijar día,
  // hora y cancha (ChallengeInboxCard). Antes iban a Mi Equipo, o sea a la
  // pantalla, no a la acción: el reporte del organizador fue justamente que
  // "la gente no encuentra dónde configurar dónde se juega".
  // Espejo en SQL: `public.notification_action_url` (v6.11.156.02).
  CHALLENGE_RECEIVED: 'dashboard',
  CHALLENGE_REMINDER: 'dashboard',
  // Éstas hablan de un partido que ya existe; el panel de Mi Equipo sigue siendo
  // su casa cuando no viajan con `related_match_id`.
  CHALLENGE_SCHEDULED: 'team',
  CHALLENGE_EXPIRED_WO: 'team',
  // v6.11.83 — lista de espera. Redundante con la marca `source: 'waitlist'` de
  // más abajo (los emite `_notify_waitlist_team`, que siempre la estampa), pero
  // acá el destino no depende de que el emisor se acuerde del metadata.
  WAITLIST_PROMOTED: 'team',
  WAITLIST_DEMOTED: 'team',
  // v6.11.209 — Avisos del día de partido de playoffs: al INICIO, donde están
  // el llamado, el presente y el partido de tu cancha. Espejo en SQL:
  // `public.notification_action_url` (v6.11.209.03).
  PLAYOFF_CALL: 'dashboard',
  PLAYOFF_COURT_CHANGE: 'dashboard',
  PLAYOFF_REMINDER: 'dashboard',
  PLAYOFF_COURT_RELEASED: 'dashboard',
  PLAYOFF_RIVAL_DEFINED: 'dashboard',
  PLAYOFF_NO_SHOW: 'dashboard',
  PLAYOFF_SHARE: 'dashboard',
  // v6.11.228 — suplencia rechazada. Normalmente viaja con action_url
  // (`/mi-equipo` al compañero, `/dashboard` al suplente, que no tiene equipo).
  SUBSTITUTION_REJECTED: 'team',
};

// v6.11.209 — Las alertas que se muestran a pantalla completa en el Inicio
// (`NoShowAlertOverlay`) y el "tu rival no dio el presente". Las anteriores a
// v6.11.209.07 viajan como `MATCH` con `action_url = '/agenda'` — la agenda de
// la fase regular, donde los playoffs no están —; por la marca van al Inicio.
const MATCH_DAY_ALERTS = new Set(['NO_SHOW', 'NO_SHOW_RIVAL', 'CALL']);

// v6.11.91 — Tipos que hablan de la PROGRAMACIÓN de un partido, no de su
// resultado. Nunca abren MatchCenter, aunque traigan `related_match_id` o un
// `action_url` de `/partido/<id>`: MatchCenter es la pantalla de cargar el
// resultado, y para un partido que todavía no se jugó eso invita a hacer algo
// que el reglamento no permite todavía (la carga tiene su ventana y su foto).
// El partido igual viaja, como `highlightMatchId`, para que la agenda lo señale.
//
// Lo que NO está acá y no debe estar: `RESULTADO_PENDIENTE` y el "⏰ Recordatorio:
// Subir resultado" (que viaja como `WARNING`), que existen justamente para que
// el jugador abra la pantalla de resultado.
const AGENDA_ONLY_MATCH_TYPES = new Set([
  'MATCH',
  'MATCH_PROPOSED',
  'MATCH_CONFIRMED',
  'MATCH_MODIFIED',
  'MATCH_CANCELLED',
  'PARTIDO_PROXIMO',
]);

// `metadata.target` de las notificaciones que ya venían con metadata de
// navegación (propose_match, admin_reject_photo).
const META_TARGET_TO_VIEW: Record<string, string> = {
  profile: 'profile',
  perfil: 'profile',
  fixture: 'schedule-agenda',
  agenda: 'schedule-agenda',
  team: 'team',
  'mi-equipo': 'team',
  standings: 'standings',
  tabla: 'standings',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && UUID_RE.test(value);

/**
 * Path de un deep link → destino. Devuelve null cuando el path no es una ruta
 * del shell del participante (`/`, `/liga/...`, `/admin-liga`, etc.): el caller
 * sigue con su propio ruteo.
 *
 * Acepta el path solo o la URL entera; ignora query string y hash (el Service
 * Worker agrega `?from=push`).
 */
export function resolveDeepLinkPath(pathname: string | null | undefined): NotificationTarget | null {
  if (!pathname) return null;

  const clean = pathname.split('?')[0].split('#')[0];
  const segments = clean.split('/').filter(Boolean).map((s) => s.toLowerCase());
  if (segments.length === 0) return null;

  // `/partido/<uuid>` → MatchCenter de ese partido. Detrás queda la agenda, que
  // es donde vuelve el jugador al cerrarlo.
  if (segments[0] === 'partido') {
    return isUuid(segments[1])
      ? { view: 'schedule-agenda', matchId: segments[1] }
      : { view: 'schedule-agenda' };
  }

  // v6.11.91 — `/agenda/<uuid>` → la agenda con ESE partido resaltado, sin abrir
  // nada. Es el destino de todo lo que pasa antes de jugar (confirmación,
  // cambio de horario, "tu partido empieza en 5 minutos").
  if (segments[0] === 'agenda' && segments.length > 1) {
    return isUuid(segments[1])
      ? { view: 'schedule-agenda', highlightMatchId: segments[1] }
      : { view: 'schedule-agenda' };
  }

  // v6.11.225 — `/evento/<uuid>` → la pantalla del evento intermedio. Es el
  // link que se comparte por WhatsApp: sin uuid válido no hay a dónde ir, así
  // que no se inventa una vista (el caller sigue con su ruteo).
  if (segments[0] === 'evento') {
    return isUuid(segments[1]) ? { view: 'league-event', eventId: segments[1] } : null;
  }

  const view = PATH_TO_VIEW[segments[0]];
  return view ? { view } : null;
}

/**
 * Notificación → destino, en orden de confianza:
 *
 *   1. `action_url`  Lo que decidió el emisor (o el trigger de la BD).
 *   2. `metadata`    Las notificaciones viejas que traían `action: 'navigate'`.
 *   3. `type`        Red de contención por familia de notificación.
 *
 * Devuelve null cuando no hay a dónde llevar al jugador (avisos generales del
 * admin, cumpleaños, etc.): la campanita los deja sin click en vez de mandarlo
 * al Inicio como si algo hubiera pasado.
 */
export function resolveNotificationTarget(
  notification: NotificationTargetInput | null | undefined
): NotificationTarget | null {
  if (!notification) return null;

  const matchId = isUuid(notification.related_match_id) ? notification.related_match_id : undefined;
  // v6.11.91 — Las de programación nunca abren la pantalla de resultado; el
  // partido las acompaña sólo para resaltarlo en la agenda.
  const agendaOnly = !!notification.type && AGENDA_ONLY_MATCH_TYPES.has(notification.type);

  const alertKind = notification.metadata?.alert;
  if (typeof alertKind === 'string' && MATCH_DAY_ALERTS.has(alertKind)) return { view: 'dashboard' };

  const fromUrl = resolveDeepLinkPath(notification.action_url);
  if (fromUrl) {
    // Legacy `/match-center`: la ruta no existía, pero el partido sí viene en la
    // fila. Con él abrimos la pantalla de resultado, que es lo que pedía el copy
    // ("Subir resultado", "Confirmá el resultado").
    const resolved =
      !fromUrl.matchId && matchId && notification.action_url?.includes('match-center')
        ? { ...fromUrl, matchId }
        : fromUrl;
    // Las ~miles de filas que ya están guardadas con `/partido/<id>` y tipo de
    // programación (MATCH_CONFIRMED es el caso reportado) se degradan acá: el
    // backfill de la migración v6.11.91 arregla las que están sin leer, pero la
    // regla no puede depender de que ninguna se haya escapado.
    if (agendaOnly && resolved.matchId) {
      const { matchId: openedMatchId, ...rest } = resolved;
      return { ...rest, highlightMatchId: openedMatchId };
    }
    return resolved;
  }

  // v6.11.209 — Igual que en SQL: un PLAYOFF_* sin action_url va al Inicio
  // ANTES de la regla que manda todo lo que tiene `finals_match_id` al cuadro.
  if (notification.type?.startsWith('PLAYOFF_')) return { view: 'dashboard' };

  const meta = notification.metadata;
  if (meta) {
    if (meta.action === 'view_playoff_schedule') return { view: 'playoffs-bracket' };
    if (meta.action === 'navigate' && typeof meta.target === 'string') {
      const view = META_TARGET_TO_VIEW[meta.target.toLowerCase()];
      if (view) {
        return agendaOnly && matchId && view === 'schedule-agenda'
          ? { view, highlightMatchId: matchId }
          : { view };
      }
    }
    // v6.11.66.02 — Marcas estructuradas de emisores que no ponen `action`.
    // Se eligen claves que sólo existen en un contexto: `source: 'waitlist'`
    // (_notify_waitlist_team) y los ids de playoffs. NO se mira el título: el
    // 60% de lo que queda sin destino son avisos que el admin redacta a mano
    // (`admin_send_bulk_notification` fuerza `type = 'INFO'` y no le deja
    // elegir destino), y adivinar por prosa se rompe en silencio la primera
    // vez que alguien cambia una palabra.
    if (meta.source === 'waitlist') return { view: 'team' };
    if (meta.finals_match_id || meta.qualified_team_id) return { view: 'playoffs-bracket' };
  }

  const byType = notification.type ? TYPE_TO_VIEW[notification.type] : undefined;
  if (!byType) return null;
  // Sin `action_url` pero con partido: la agenda lo resalta igual.
  if (agendaOnly && matchId) return { view: byType, highlightMatchId: matchId };
  return { view: byType };
}
